import React, { DetailedHTMLProps, ImgHTMLAttributes } from 'react';
import { AlgoliaProductCondition, AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { ProductCardProps, ProductVariant } from '../../ProductCard/types';
import { format } from 'date-fns';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase/toCamelCase';
import { TrackingDescriptorType } from '../../Tracking/types';
import { transformAlgoliaHitForTracking } from './transformAlgoliaHitForTracking';
import resizeShopifyImage from '@jbhi-fi/utils/src/resizeShopifyImage/resizeShopifyImage';
import { UseProgressiveImage } from '../../hooks/useProgressiveImage/useProgressiveImage';
import { getPromoTags } from '../../ProductCard/utils/getTags';
import { MarketplaceSellerCalloutProps } from '../../Marketplace/MarketplaceSellerCallout/MarketplaceSellerCallout';
import { getDeliveryTag } from '@jbhi-fi/utils/src/getDeliveryTag/getDeliveryTag';
import { GIFTCARDSKU } from '@jbhi-fi/utils/src/shopify/skus';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { getPerksDealStatusFromTags } from '@jbhi-fi/utils/src/perks/getPerksDealStatusFromTags';

export const MISSING_VARIANT_ID = 0;
const RELEASE_DATE_TEXT_CUTOFF_DAYS = 14;
const PROMO_TAGS_COUNT = 2;
export const CD_CATEGORY_ID = 1366;
export const VINYL_CATEGORY_ID = 1368;

type AlgoliaHitWithMarketplaceSeller = Omit<AlgoliaProductHit, 'tieredDetails' | 'product'> & {
  tieredDetails: Required<AlgoliaProductHit>['tieredDetails'];
  product: Omit<Required<AlgoliaProductHit>['product'], 'seller'> & {
    seller: Required<Required<AlgoliaProductHit>['product']>['seller'];
  };
};

const isMusicCategory = (categoryId: number): boolean => {
  return [VINYL_CATEGORY_ID, CD_CATEGORY_ID].includes(categoryId);
};

const getReleaseDate = (hit: AlgoliaProductHit): string => {
  const releaseDate = new Date(Number(hit.release_date) * 1000);
  const daysUntilRelease = (releaseDate.getTime() - Date.now()) / (1000 * 3600 * 24);
  if (hit.product?.releaseDateText && daysUntilRelease > RELEASE_DATE_TEXT_CUTOFF_DAYS) {
    return hit.product.releaseDateText;
  }
  return format(releaseDate, 'd MMM yy');
};

export const getSkuForVariant = (hit: AlgoliaProductHit, variantId?: number) => {
  if (hit.sku) return hit.sku;
  if (hit.children) {
    const child = hit.children.find(child => child.variant_id === variantId);
    if (child) return child.SKU;
  }
  return hit.skus?.[0] ?? '';
};

export type MiniProductInfoVariant = 'None' | 'Format' | 'Release Date';

type ToMiniProductTileOptions = {
  onClick?: (e: React.MouseEvent, hit: AlgoliaProductHit) => void;
  infoVariant?: MiniProductInfoVariant;
  number?: number;
};

export abstract class AlgoliaHitTransformer {
  static toProductVariants = (hit: AlgoliaProductHit) => {
    const variants: Array<ProductVariant> = [];
    if (hit.options?.length) {
      const currentOption = hit.options[0];
      [...new Set(hit.options[0].Values)].forEach(option => {
        const matchedIndex = hit.children?.findIndex(child => child?.Values?.[0] === option);
        const matchedVariant = matchedIndex != null ? hit.children?.[matchedIndex] : null;

        if (matchedVariant?.Display) {
          variants.push({
            sku: matchedVariant.SKU,
            button: toCamelCase(matchedVariant.Button),
            variantId: matchedVariant.variant_id,
            productImage: matchedVariant.Image,
            specRender: currentOption.SpecRender,
            variantValue: option,
            sequence: matchedVariant.Sequence ?? matchedIndex,
            handle: matchedVariant.handle,
          });
        }
      });
    }
    variants.sort((a, b) => a.sequence - b.sequence);
    return variants;
  };

  static toProductCardProps = (
    hit: AlgoliaProductHit,
    options: { trackingDescriptor?: TrackingDescriptorType } = {}
  ): ProductCardProps => {
    const {
      all_calculated_product_tags: productTags,
      brand_image_url: brandImageUrl,
      banner_tags: bannerTag,
      button,
      deliveryTag,
      display,
      handle,
      is_family: isFamily,
      isSponsored,
      pricing,
      perksPricing,
      product,
      productGroup,
      product_image: productImage,
      promo_tags: promoTags = [],
      preamble,
      priceBannerTag,
      showPerksPrice,
      sku,
      title,
      variant_id: variantId,
    } = hit;

    const {
      id = `${hit.product_id ?? hit.variant_id ?? hit.sku}`,
      showReviews: shouldShowReviews,
      averageProductReviewRating,
      numberOfProductReviews,
    } = hit.product || {};
    const shouldDisplayWasPrice = hit.pricing?.displayWasPrice;
    const artist = preamble ? preamble : display?.artist;
    const productVariants = AlgoliaHitTransformer.toProductVariants(hit);
    const isPerksMember = window.Jbhifi?.customerDetails?.isPerks ?? false;
    const { isPerksEarlyAccess, isPerksExclusiveDeal } = getPerksDealStatusFromTags(productTags ?? []);
    const shouldHidePerksExclusiveDealsFromNonMembers = window.Jbhifi?.shouldHidePerksExclusiveDealsFromNonMembers;
    const hiddenPreambleProductGroupIds = window.Jbhifi?.hiddenProductCardSubtextProductGroupIds?.split(',');
    const priceBannerLabel = priceBannerTag?.label;
    const marketplaceSeller = AlgoliaHitTransformer.toMarketplaceSellerCalloutProps(hit);
    const disclaimers = window.Jbhifi?.disclaimers ?? [];

    return {
      ...product,
      artist,
      averageProductReviewRating,
      bannerLabel: bannerTag?.label,
      brandImageUrl,
      button: toCamelCase(button),
      deliveryTag: getDeliveryTag(deliveryTag, product?.freightPrice),
      departmentCode: product?.departmentCode,
      handle,
      isFamily,
      isSponsored,
      keyFeatures: display?.keyFeatures?.length ? display.keyFeatures : [],
      numberOfProductReviews,
      preamble,
      pricing,
      perksPricing,
      productGroupId: productGroup?.id,
      productId: id,
      productImage,
      productTags,
      productVariants,
      promoTags: getPromoTags(promoTags)?.slice(0, PROMO_TAGS_COUNT) ?? [],
      priceBannerLabel,
      algoliaQueryId: hit.__queryID,
      shouldShowReviews,
      shouldDisplayWasPrice,
      showPerksPrice,
      shouldHidePerksExclusiveDealsFromNonMembers,
      shouldHideCartOverlay: sku === GIFTCARDSKU,
      sku,
      title,
      variantId: isFamily ? productVariants?.[0]?.variantId : variantId || MISSING_VARIANT_ID,
      trackingInformation: options?.trackingDescriptor
        ? {
            gtm: hit.google_tag_manager,
            trackingDescriptor: options.trackingDescriptor,
            criteoProduct: hit.criteoProduct,
            currency: getStoreCurrency(),
            ga4: {
              productVariants,
              averageProductReviewRating,
              numberOfProductReviews: numberOfProductReviews ?? 0,
              transformedGtmData: transformAlgoliaHitForTracking(hit, {
                listName: options.trackingDescriptor?.ecommerceExtraProps?.list,
                listSlot: options.trackingDescriptor?.ecommerceExtraProps?.itemListSlot,
                listServiceConfig: options.trackingDescriptor?.ecommerceExtraProps?.itemListServiceConfig,
                startIndex: options.trackingDescriptor?.ecommerceExtraProps?.position,
              })?.[0],
            },
          }
        : undefined,
      isPerksMember,
      isPerksEarlyAccess,
      isPerksExclusiveDeal,
      hiddenPreambleProductGroupIds,
      marketplaceSeller,
      toolTips: disclaimers,
      tooltipVariant: 'medium',
    };
  };

  static toMiniProductTileProps = (algoliaProductHit: AlgoliaProductHit, options: ToMiniProductTileOptions = {}) => {
    const { onClick = () => undefined, infoVariant = 'None', number } = options;
    const isMusic = isMusicCategory(algoliaProductHit.category_id);

    return {
      hit: algoliaProductHit,
      onClick,
      heading: algoliaProductHit.title,
      subheading: isMusic ? (algoliaProductHit.preamble ?? algoliaProductHit.display.artist) : undefined,
      number,
      footer:
        infoVariant === 'Format' && !isMusic
          ? algoliaProductHit.preamble
          : infoVariant === 'Release Date'
            ? getReleaseDate(algoliaProductHit)
            : undefined,
    };
  };

  static toImageProps = (
    hit: {
      product_image: string;
      title: string;
    },
    { sizePx = 300 }: { sizePx?: number } = {}
  ): DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> => {
    const getImageSize = (size: number) => `${size}x${size}`;

    return {
      src: resizeShopifyImage(hit.product_image, getImageSize(sizePx * 2)),
      alt: hit.title,
      width: sizePx,
      height: sizePx,
      srcSet: [
        `${resizeShopifyImage(hit.product_image, getImageSize(sizePx))}`,
        `${resizeShopifyImage(hit.product_image, getImageSize(Math.ceil(sizePx * 1.5)))} 1.5x`,
        `${resizeShopifyImage(hit.product_image, getImageSize(sizePx * 2))} 2x`,
        `${resizeShopifyImage(hit.product_image, getImageSize(sizePx * 3))} 3x`,
      ].join(','),
    };
  };

  static toProgressiveImageProps = (
    ...[hit, options]: Parameters<typeof this.toImageProps>
  ): Parameters<UseProgressiveImage>[0] => {
    return {
      ...AlgoliaHitTransformer.toImageProps(hit, options),
      placeholderSrc: resizeShopifyImage(hit.product_image, '32x32'),
    };
  };

  static isMarketplaceProduct = (hit: AlgoliaProductHit): hit is AlgoliaHitWithMarketplaceSeller => {
    return Boolean(hit?.product?.seller?.id) && Boolean(hit.tieredDetails?.[0]?.seller?.id);
  };

  static toMarketplaceSellerCalloutProps = (
    hit: AlgoliaProductHit
  ): Pick<MarketplaceSellerCalloutProps, 'sellerName' | 'sellerHref'> | undefined => {
    if (!AlgoliaHitTransformer.isMarketplaceProduct(hit)) {
      return undefined;
    }

    return {
      sellerName: hit.product.seller.name,
    };
  };

  static toMarketplaceSeller = (
    hit: AlgoliaProductHit
  ):
    | {
        name: string;
        id: string;
        isDigitalInvoiceProvided?: boolean | null;
        tieredIds: Array<string>;
        productCondition: AlgoliaProductCondition;
      }
    | undefined => {
    if (!AlgoliaHitTransformer.isMarketplaceProduct(hit)) {
      return undefined;
    }
    const tieredDetails = hit.tieredDetails?.filter(
      tieredDetail => Boolean(tieredDetail.seller?.id) && tieredDetail.seller.id === hit.product.seller.id
    );

    return {
      name: hit.product.seller.name,
      id: hit.product.seller.id,
      tieredIds: tieredDetails?.map(tieredDetail => tieredDetail.id) ?? [],
      productCondition: tieredDetails?.[0]?.condition ?? {},
      isDigitalInvoiceProvided: null,
    };
  };
}
